import type { SwitchProps } from './Switch';

/**
 * Maps the bare MUI `Switch` props onto the Design System 2.0 API.
 *
 * TODO(HNS-264): temporary. This mapper exists only so the visual refresh can be gated inside the
 * wrapper instead of at its call sites; retiring the flag means deleting it and letting call sites
 * speak the DS vocabulary directly.
 *
 * Returning `null` means "the DS switch cannot express this one" and sends the caller back to MUI.
 *
 * This is the STANDALONE switch: no label of its own, so the name has to come from `aria-label` or
 * `aria-labelledby`. A switch that sits inside a `FormControlLabel` must not reach this branch,
 * because the DS switch is itself a `<label>` and the two would nest — `ReactHookForm/Switch` and
 * `ChatModerationSwitch` therefore render the MUI switch directly, and own their own DS branch.
 *
 * Cases the DS switch cannot express, all of which stay on MUI:
 *
 *   - `size` other than `small`, INCLUDING an omitted one — mapped by measured height, as
 *     `ReactHookForm/Switch/toDsProps` records: MUI `small` is a 2px match, `medium` is not.
 *   - `sx` — an Emotion prop with no effect on a Tailwind control.
 *   - a non-string `value`, which the DS control cannot take.
 *   - any other key (`className`, `color`, `inputProps`, `slotProps`, `required`, `readOnly`, `data-*`, `edge`):
 *     the wrapper forwards only the handled set, so anything else would be dropped silently. The type
 *     names no such key, but a `data-*` attribute or a spread can still carry one at runtime.
 *   - no accessible name. A switch announced as just "switch" is a defect rather than a cosmetic
 *     gap, so the DS branch refuses to draw one (`AutoOptOutSwitch` used to be exactly that, and now
 *     passes `aria-label`).
 */
/** One name source exactly, mirroring the union in `components/Switch/types.ts` so the wrapper can spread it uncast. */
export type DsProps = { 'aria-labelledby': string } | { 'aria-label': string };

export const toDsProps = ({
  size,
  sx,
  value,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  // Forwarded by the wrapper, so not this mapper's business.
  checked: _checked,
  defaultChecked: _defaultChecked,
  disabled: _disabled,
  id: _id,
  name: _name,
  onChange: _onChange,
  ...unhandled
}: SwitchProps): DsProps | null => {
  if (sx || size !== 'small') {
    return null;
  }

  if (Object.values(unhandled).some((prop) => prop !== undefined)) {
    return null;
  }

  // MUI types the input `value` as `unknown`; the DS control takes a string and a cast could let an object through.
  if (value !== undefined && typeof value !== 'string') {
    return null;
  }

  if (ariaLabelledBy) {
    return { 'aria-labelledby': ariaLabelledBy };
  }

  if (ariaLabel) {
    return { 'aria-label': ariaLabel };
  }

  return null;
};
